/** Spatial calibration and number formatting. */

export type SpatialUnit = 'px' | 'µm' | 'mm' | 'cm' | 'm' | 'deg' | 'arcmin' | 'mrad';

export const SPATIAL_UNITS: { id: SpatialUnit; label: string; angular: boolean }[] = [
  { id: 'px', label: 'pixels', angular: false },
  { id: 'µm', label: 'micrometres', angular: false },
  { id: 'mm', label: 'millimetres', angular: false },
  { id: 'cm', label: 'centimetres', angular: false },
  { id: 'm', label: 'metres', angular: false },
  { id: 'deg', label: 'degrees', angular: true },
  { id: 'arcmin', label: 'arc minutes', angular: true },
  { id: 'mrad', label: 'milliradians', angular: true },
];

export interface SpatialCalibration {
  unit: SpatialUnit;
  /** Size of one pixel in `unit` (ignored for 'px'). */
  perPixel: number;
}

export const DEFAULT_SPATIAL: SpatialCalibration = { unit: 'px', perPixel: 1 };

export function unitScale(s: SpatialCalibration): number {
  return s.unit === 'px' ? 1 : s.perPixel;
}

/** Spatial frequency unit label, e.g. cycles/mm or cycles/deg. */
export function freqUnit(s: SpatialCalibration): string {
  return `cyc/${s.unit}`;
}

/** Format a value with `sig` significant digits; switches to exponent for very large/small magnitudes. */
export function fmt(v: number | null | undefined, sig = 5): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (!Number.isFinite(v)) return v > 0 ? '∞' : '−∞';
  if (v === 0) return '0';
  const a = Math.abs(v);
  if (a >= 1e6 || a < 1e-4) return v.toExponential(Math.max(0, sig - 1)).replace(/-/g, '−');
  const s = Number(v.toPrecision(sig)).toString();
  return s.startsWith('-') ? '−' + s.slice(1) : s;
}

/** Machine-readable number for exports: plain JS representation, '.' decimal, NaN -> empty. */
export function num(v: number | null | undefined, sig = 9): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  return Number(v.toPrecision(sig)).toString();
}

export function fmtPct(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(digits)} %`;
}
